import type { ReactElement } from 'react';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

import { RoxyClearHero } from './RoxyClearHero.js';
import { useRoxy } from './RoxyProvider.js';
import './RoxyPage.css';

const COMMANDS = [
  'startPublic',
  'startParty',
  'startPrivate',
  'endPrivate',
  'endShow',
  'mute',
  'ban',
  'camera',
  'setPrivate',
  'thank',
] as const;

export function RoxyPage(): ReactElement {
  const { listenEnabled, listening, supported, setListenEnabled } = useRoxy();

  return (
    <section className="roxy-page" aria-labelledby="roxy-page-title">
      <header className="roxy-page__hero">
        <RoxyClearHero />
        <div>
          <p className="roxy-page__kicker">{t('common.studio.roxy.kicker')}</p>
          <h1 id="roxy-page-title" className="roxy-page__title">
            {t('common.studio.roxy.title')}
          </h1>
          <p className="roxy-page__lede">{t('common.studio.roxy.lede')}</p>
        </div>
      </header>

      <div className="fm-card roxy-page__listen">
        <div>
          <h2>{t('common.studio.roxy.listenTitle')}</h2>
          <p>{t('common.studio.roxy.listenHint')}</p>
          <p className="roxy-page__status">
            {listenEnabled
              ? listening
                ? t('common.studio.live.roxyListening')
                : t('common.studio.live.roxyOn')
              : t('common.studio.live.roxyOff')}
          </p>
        </div>
        <Button
          label={
            listenEnabled ? t('common.studio.settings.voiceListenOn') : t('common.studio.settings.voiceListenOff')
          }
          disabled={!supported}
          onClick={() => {
            setListenEnabled(!listenEnabled);
          }}
        />
        {!supported ? (
          <p className="roxy-page__note">{t('common.studio.settings.voiceUnsupported')}</p>
        ) : null}
      </div>

      <div className="fm-card">
        <h2>{t('common.studio.roxy.howTitle')}</h2>
        <ol className="roxy-page__steps">
          <li>{t('common.studio.roxy.howStep1')}</li>
          <li>{t('common.studio.roxy.howStep2')}</li>
          <li>{t('common.studio.roxy.howStep3')}</li>
        </ol>
        <p className="roxy-page__note">{t('common.studio.settings.voiceDesktop')}</p>
      </div>

      <div className="fm-card">
        <h2>{t('common.studio.settings.voiceCommandsTitle')}</h2>
        <p className="roxy-page__note">{t('common.studio.roxy.commandsHint')}</p>
        <table className="roxy-page__table">
          <thead>
            <tr>
              <th>{t('common.studio.settings.voiceSay')}</th>
              <th>{t('common.studio.settings.voiceDoes')}</th>
            </tr>
          </thead>
          <tbody>
            {COMMANDS.map((id) => (
              <tr key={id}>
                <td>{t(`common.studio.settings.voiceCmd.${id}.say`)}</td>
                <td>{t(`common.studio.settings.voiceCmd.${id}.does`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="fm-card">
        <h2>{t('common.studio.roxy.safetyTitle')}</h2>
        <ul className="roxy-page__safety">
          <li>{t('common.studio.roxy.safetyConfirm')}</li>
          <li>{t('common.studio.roxy.safetyManual')}</li>
          <li>{t('common.studio.roxy.safetyHolds')}</li>
        </ul>
      </div>
    </section>
  );
}
